import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, EditPane, toast } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type ClientMailingAddressDTO, type ClientNoteDTO } from "../../api";
import { checkboxField, crudEditor, selectField, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * One client, its mailing addresses and its notes.
 *
 * Saving an address re-syncs the client to Shopify, because the address's
 * `gid` / `company_location_gid` only exist after a round-trip — the Vaadin
 * grid did the same, and without it a freshly added address can't be used on
 * an order.
 */
export function ClientDetailPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const { id } = useParams({ from: "/config/client/$id" });
  const clientId = Number(id);
  const allowed = useAllowed(LogicView.CONFIG_CLIENT);
  const title = t("navigation.item.clients", "Clients");

  const client = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => api.clients.findById(clientId),
    enabled: allowed && Number.isFinite(clientId),
  });

  const countries = useQuery({
    queryKey: ["country"],
    queryFn: () => api.countries.findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });

  const countryList = countries.data ?? [];
  const addresses = client.data?.addresses ?? [];
  const notes = client.data?.notes ?? [];

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["client", clientId] });
    await queryClient.invalidateQueries({ queryKey: ["client"] });
  };

  /** Re-sync after any address write, then refresh — mirrors `syncAndRefresh`. */
  const syncAndRefresh = async () => {
    try {
      await api.clients.sync(clientId);
    } catch {
      // A sync failure must not lose the address that was just saved; the row
      // is persisted either way and the next sync picks it up.
      toast.error(t("notification.sync.clients.error", "Client sync failed"));
    }
    await refresh();
  };

  const addressColumns = useMemo(() => {
    const c = defineColumns<ClientMailingAddressDTO>();
    return [
      c.text("first_name", (row) => row.first_name, {
        headerKey: "column.first-name",
        priority: 2,
      }),
      c.text("last_name", (row) => row.last_name, { headerKey: "column.last-name", priority: 2 }),
      c.text("company", (row) => row.company, { headerKey: "column.company", priority: 1 }),
      c.text("address1", (row) => row.address1, { headerKey: "column.address1", priority: 1 }),
      c.text("address2", (row) => row.address2, { headerKey: "column.address2", priority: 5 }),
      c.text("country_code", (row) => row.country_code, {
        headerKey: "column.country.code",
        filter: "select",
        priority: 4,
      }),
      c.text("province_code", (row) => row.province_code, {
        headerKey: "column.province.code",
        filter: "select",
        priority: 4,
      }),
      c.text("city", (row) => row.city, { headerKey: "column.city", priority: 3 }),
      c.text("zip", (row) => row.zip, { headerKey: "column.zip", priority: 4 }),
      c.text("phone", (row) => row.phone, { headerKey: "column.phone", priority: 5 }),
      c.custom(
        "is_default",
        (row) => String(row.is_default),
        (row) => (row.is_default ? <Badge variant="accent">✓</Badge> : <span>—</span>),
        { headerKey: "column.default", filter: "select", priority: 3 },
      ),
    ];
  }, []);

  const addressEditor = useMemo(
    () =>
      crudEditor<ClientMailingAddressDTO>({
        client: api.clientAddresses,
        refresh: syncAndRefresh,
        t,
        fields: {
          first_name: textField(
            (row) => row.first_name,
            (row, value) => ({ ...row, first_name: value }),
          ),
          last_name: textField(
            (row) => row.last_name,
            (row, value) => ({ ...row, last_name: value }),
          ),
          company: textField(
            (row) => row.company,
            (row, value) => ({ ...row, company: value }),
          ),
          address1: textField(
            (row) => row.address1,
            (row, value) => ({ ...row, address1: value }),
            true,
          ),
          address2: textField(
            (row) => row.address2,
            (row, value) => ({ ...row, address2: value }),
          ),
          country_code: selectField(
            (row) => row.country_code,
            // Changing country invalidates the province, so clear it rather
            // than leave a code that belongs to the previous country.
            (row, value) => ({ ...row, country_code: value, province_code: null }),
            countryList.map((entry) => ({
              value: entry.iso_3166_code,
              label: `${entry.iso_3166_code} - ${entry.name}`,
            })),
          ),
          province_code: selectField(
            (row) => row.province_code,
            (row, value) => ({ ...row, province_code: value }),
            (row) =>
              (countryList.find((entry) => entry.iso_3166_code === row.country_code)?.regions ?? [])
                .map((region) => ({
                  value: region.iso_3166_code,
                  label: `${region.iso_3166_code} - ${region.name}`,
                })),
          ),
          city: textField(
            (row) => row.city,
            (row, value) => ({ ...row, city: value }),
            true,
          ),
          zip: textField(
            (row) => row.zip,
            // Zips are stored uppercase and unspaced, matching StringUtil.
            (row, value) => ({ ...row, zip: value.toUpperCase().replace(/\s/g, "") }),
            true,
          ),
          phone: textField(
            (row) => row.phone,
            (row, value) => ({ ...row, phone: value }),
          ),
          is_default: checkboxField(
            (row) => row.is_default,
            (row, value) => ({ ...row, is_default: value }),
          ),
        },
        newRow: () => ({
          id: 0,
          client_id: clientId,
          is_default: addresses.length === 0,
          first_name: "",
          last_name: "",
          company: "",
          address1: "",
          address2: "",
          country_code: "",
          province_code: "",
          city: "",
          zip: "",
          phone: "",
        }),
        validate: (row) => {
          const errors: Record<string, string> = {};
          const required = t("form.validation.field.required", "Value is required");
          // Either a person or a company has to identify the address.
          const named = Boolean(row.first_name?.trim() && row.last_name?.trim());
          if (!named && !row.company?.trim()) {
            errors.company = required;
            if (!row.first_name?.trim()) errors.first_name = required;
            if (!row.last_name?.trim()) errors.last_name = required;
          }
          if (!row.address1?.trim()) errors.address1 = required;
          if (!row.city?.trim()) errors.city = required;
          if (!row.country_code) errors.country_code = required;
          if (!row.province_code) errors.province_code = required;
          if (!row.zip?.trim()) errors.zip = required;
          return Object.keys(errors).length > 0 ? errors : null;
        },
        onSave: async (row, isNew) => {
          // Exactly one default per client: clear the others first, as the
          // Vaadin grid did before writing.
          if (row.is_default) {
            for (const address of addresses) {
              if (address.id !== row.id && address.is_default) {
                await api.clientAddresses.update({ ...address, is_default: false });
              }
            }
          }
          if (isNew) await api.clientAddresses.create(row);
          else await api.clientAddresses.update(row);
        },
      }),
    [addresses, api, clientId, countryList, t],
  );

  const noteColumns = useMemo(() => {
    const c = defineColumns<ClientNoteDTO>();
    return [c.text("note", (row) => row.note, { headerKey: "column.note", priority: 1 })];
  }, []);

  const noteEditor = useMemo(
    () =>
      crudEditor<ClientNoteDTO>({
        client: api.clientNotes,
        refresh,
        t,
        fields: {
          note: textField(
            (row) => row.note,
            (row, value) => ({ ...row, note: value }),
            true,
          ),
        },
        newRow: () => ({ id: 0, client_id: clientId, account_id: 0, note: "" }),
        validate: (row) =>
          row.note.trim() ? null : { note: t("form.validation.field.required", "Value is required") },
      }),
    [api, clientId, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (client.error) return <ErrorState title={title} error={client.error} />;

  const entity = client.data;

  return (
    <>
      <PageHeader
        parent={{ to: "/config/client", label: t("navigation.item.clients", "Clients") }}
        title={`${entity ? `${entity.code} - ${entity.name}` : `#${clientId}`}`}
        badges={
          entity ? (
            <div className="flex items-center gap-1.5">
              <Badge variant="info">{entity.type}</Badge>
              <Badge variant={entity.currency === "USD" ? "success" : "neutral"}>
                {entity.currency}
              </Badge>
              {entity.customer_gid && <Badge variant="accent">Shopify linked</Badge>}
            </div>
          ) : undefined
        }
      />

      <EditPane
        tabs={[
          {
            id: "addresses",
            flush: true,
            label: t("tab.addresses", "Addresses"),
            badge: <Badge size="sm">{addresses.length}</Badge>,
            content: (
              <DataTable<ClientMailingAddressDTO>
                data={addresses}
                columns={addressColumns}
                getRowId={(row) => String(row.id)}
                loading={client.isLoading}
                editor={addressEditor}
              />
            ),
          },
          {
            id: "notes",
            flush: true,
            label: t("tab.notes", "Notes"),
            badge: <Badge size="sm">{notes.length}</Badge>,
            content: (
              <DataTable<ClientNoteDTO>
                data={notes}
                columns={noteColumns}
                getRowId={(row) => String(row.id)}
                loading={client.isLoading}
                editor={noteEditor}
              />
            ),
          },
        ]}
      />
    </>
  );
}
